"use client"

import { Suspense, useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'next/navigation'
import dynamic from 'next/dynamic'
import { CustomAvatarPage } from '@/layouts/custom-avatar-page'
import { KioskAvatarPage } from '@/layouts/kiosk-avatar-page'

// 태권낭자 리디자인(expo2 다크 테마)을 공개 슬러그 페이지에 적용할 에이전트 목록
// SSR 비활성화 필수 — 레이아웃 임포트 체인에 렌더 시점 window 접근이 있음 (expo2 라우트와 동일)
const EXPO2_REDESIGN_AGENT_IDS = [
  '34d19558-8ce5-4811-b152-65e90ac80461', // 태권낭자
]
const Expo2AvatarPage = dynamic(
  () => import('@/layouts/expo2-avatar-page').then((m) => m.Expo2AvatarPage),
  { ssr: false }
)
import { KioskCoverModal } from '@/layouts/kiosk-avatar-page/ui/kiosk-cover-modal'
import { useRealtimeAgentStore } from '@/shared/store/realtime-agent'
import { useAvatarStore } from '@/shared/store/avatar-store'
import { AgentConnectionManager } from '@/entities/realtime-agent/model/use-agent-connection'
import { Loader2 } from 'lucide-react'

/**
 * 공개 슬러그 기반 3D 아바타 채팅 페이지
 *
 * 사용자가 설정한 커스텀 슬러그로 접근하는 공개 페이지입니다.
 * - URL: /{slug} → 웹 모드
 * - URL: /{slug}?mode=kiosk → 키오스크 모드
 * - 인증 없이 누구나 접근 가능
 * - 슬러그 → 에이전트 ID 변환 후 모드별 페이지 렌더링
 *
 * IMPORTANT: SSR 안전성
 * - Zustand subscribeWithSelector 미들웨어가 SSR에서 useSyncExternalStore 문제를 일으킴
 * - 스토어 접근은 반드시 useEffect 내부 또는 클라이언트 마운트 후에만 수행
 * - useSearchParams()는 Suspense 경계 내에서 사용해야 함
 *
 * 로딩 UI 통합 (키오스크 모드):
 * - 키오스크 모드에서는 KioskCoverModal을 로딩 UI로 사용
 * - 화면 전환 없이 일관된 UX 제공
 *
 * 예시:
 * - /my-ai-bot → 해당 슬러그를 가진 에이전트의 아바타 페이지 (웹 모드)
 * - /my-ai-bot?mode=kiosk → 키오스크 모드
 */

// 웹 모드 로딩 폴백 컴포넌트
function LoadingFallback() {
  return (
    <div className="w-full h-dvh flex items-center justify-center bg-gradient-to-br from-purple-50 to-pink-50">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500 mx-auto mb-4" />
        <p className="text-gray-500">페이지 로딩 중...</p>
      </div>
    </div>
  )
}

// 키오스크 모드 로딩 폴백 컴포넌트 - KioskCoverModal 사용
function KioskLoadingFallback() {
  return (
    <div className="w-full h-dvh relative overflow-hidden">
      <KioskCoverModal
        isOpen={true}
        isLoading={true}
        loadingMessage="페이지 로딩 중..."
        onStart={() => {}} // 로딩 중에는 비활성화
      />
    </div>
  )
}

// 키오스크 모드 404 에러 컴포넌트
function KioskNotFoundError() {
  return (
    <div className="w-full h-dvh fixed inset-0 z-[100] bg-black">
      <video
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover opacity-30"
      >
        <source src="/webm/cover.webm" type="video/webm" />
      </video>
      <div className="relative z-10 flex items-center justify-center h-full">
        <div className="text-center text-white">
          <h1 className="text-6xl font-bold mb-4">404</h1>
          <p className="text-xl">페이지를 찾을 수 없습니다.</p>
          <p className="text-white/60 mt-2">
            URL을 확인하거나 QR 코드를 다시 스캔해주세요.
          </p>
        </div>
      </div>
    </div>
  )
}

// 키오스크 모드 일반 에러 컴포넌트
function KioskErrorFallback() {
  return (
    <div className="w-full h-dvh fixed inset-0 z-[100] bg-black">
      <video
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover opacity-30"
      >
        <source src="/webm/cover.webm" type="video/webm" />
      </video>
      <div className="relative z-10 flex items-center justify-center h-full">
        <div className="text-center text-white">
          <p className="text-xl">오류가 발생했습니다.</p>
          <p className="text-white/60 mt-2">잠시 후 다시 시도해주세요.</p>
        </div>
      </div>
    </div>
  )
}

// Suspense로 감싸는 래퍼 컴포넌트
export default function SlugAvatarPage() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <SlugAvatarPageContent />
    </Suspense>
  )
}

// 실제 페이지 콘텐츠
function SlugAvatarPageContent() {
  const params = useParams()
  const searchParams = useSearchParams()
  const slug = params?.slug as string

  // URL 쿼리 파라미터에서 모드 결정: ?mode=kiosk이면 키오스크 모드
  const isKioskMode = searchParams?.get('mode') === 'kiosk'

  const [agentId, setAgentIdState] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isReady, setIsReady] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const abortController = new AbortController()

    const fetchAgentBySlug = async () => {
      if (!slug) return

      try {
        setIsLoading(true)
        setError(null)

        // 백엔드 API 직접 호출 (Server Action 대신)
        const response = await fetch(`/api/public/s/${encodeURIComponent(slug)}`, {
          signal: abortController.signal,
        })

        if (cancelled) return

        if (!response.ok) {
          if (response.status === 404) {
            setError('not_found')
            return
          }
          throw new Error('에이전트 조회 실패')
        }

        const data = await response.json()
        if (cancelled) return

        if (data?.id) {
          // SSR 안전: useEffect 내부에서 스토어 직접 접근
          const store = useRealtimeAgentStore.getState()
          const avatarStore = useAvatarStore.getState()

          // slug/mode 전환 시 이전 agent에 바인딩된 소켓을 복구하지 않는다.
          AgentConnectionManager.reset()
          store.reset()
          // 아바타 스토어도 리셋 (3D 렌더링 재초기화 필수!)
          avatarStore.reset()

          // 새로운 agentId 설정
          store.setAgentId(data.id)

          // 웹/키오스크 모드 설정 (중요!)
          const displayMode = isKioskMode ? 'kiosk' : 'web'
          store.setDisplayMode(displayMode)
          console.log('📺 [Slug] Display Mode 설정:', displayMode)

          setAgentIdState(data.id)
          setIsReady(true)
          console.log('🔗 [Slug] 공개 슬러그 페이지 진입:', slug, '→', data.id, '모드:', displayMode)
        } else {
          setError('not_found')
        }
      } catch (err) {
        if (cancelled || (err instanceof DOMException && err.name === 'AbortError')) return
        console.error('슬러그 조회 실패:', err)
        setError('error')
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    void fetchAgentBySlug()
    return () => {
      cancelled = true
      abortController.abort()
    }
  }, [slug, isKioskMode])

  // 로딩 상태 - 모드에 따라 다른 로딩 UI
  if (isLoading) {
    return isKioskMode ? <KioskLoadingFallback /> : (
      <div className="w-full h-dvh flex items-center justify-center bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-purple-500 mx-auto mb-4" />
          <p className="text-gray-500">페이지 로딩 중...</p>
        </div>
      </div>
    )
  }

  // 에러 상태 (404) - 모드에 따라 다른 에러 UI
  if (error === 'not_found') {
    return isKioskMode ? <KioskNotFoundError /> : (
      <div className="w-full h-dvh flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <h1 className="text-6xl font-bold text-gray-300 mb-4">404</h1>
          <p className="text-gray-500 text-lg">페이지를 찾을 수 없습니다.</p>
          <p className="text-gray-400 text-sm mt-2">
            URL을 확인하거나 QR 코드를 다시 스캔해주세요.
          </p>
        </div>
      </div>
    )
  }

  // 일반 에러 상태 - 모드에 따라 다른 에러 UI
  if (error) {
    return isKioskMode ? <KioskErrorFallback /> : (
      <div className="w-full h-dvh flex items-center justify-center bg-gradient-to-br from-red-50 to-pink-50">
        <div className="text-center">
          <p className="text-gray-500 text-lg">오류가 발생했습니다.</p>
          <p className="text-gray-400 text-sm mt-2">잠시 후 다시 시도해주세요.</p>
        </div>
      </div>
    )
  }

  // agentId가 없으면 에러
  if (!agentId) {
    return isKioskMode ? <KioskErrorFallback /> : (
      <div className="w-full h-dvh flex items-center justify-center bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="text-center">
          <p className="text-gray-500 text-lg">에이전트 ID가 필요합니다.</p>
          <p className="text-gray-400 text-sm mt-2">올바른 URL을 확인해주세요.</p>
        </div>
      </div>
    )
  }

  // 스토어 초기화 대기 - 모드에 따라 다른 로딩 UI
  if (!isReady) {
    return isKioskMode ? <KioskLoadingFallback /> : (
      <div className="w-full h-dvh flex items-center justify-center bg-gradient-to-br from-purple-50 to-pink-50">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    )
  }

  // URL 쿼리 파라미터에 따라 렌더링 분기
  if (isKioskMode) {
    return <KioskAvatarPage agentId={agentId} />
  }

  // 태권낭자: expo2 리디자인(다크 테마) 레이아웃 사용
  if (EXPO2_REDESIGN_AGENT_IDS.includes(agentId)) {
    return <Expo2AvatarPage agentId={agentId} />
  }

  return <CustomAvatarPage agentId={agentId} />
}
